import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { observerBackendOrigin } from "../services/runtimeConfig";

const base = observerBackendOrigin + "/api/observer/admin";
const headers = () => ({
  Authorization: "Bearer " + (localStorage.getItem("apiToken") || ""),
  "X-Actor": localStorage.getItem("observerUserEmail") || "",
});

export default function ObserverAdminPage() {
  const [items, setItems] = useState<any[]>([]),
    [status, setStatus] = useState<any>(),
    [legal, setLegal] = useState<any>(),
    [error, setError] = useState("");
  const [queuePage, setQueuePage] = useState(1),
    [queueTotal, setQueueTotal] = useState(0),
    [queueTotalPages, setQueueTotalPages] = useState(0);
  const [token, setToken] = useState(localStorage.getItem("apiToken") || ""),
    [staff, setStaff] = useState(
      localStorage.getItem("observerUserEmail") || "",
    ),
    [discoveryMaximum, setDiscoveryMaximum] = useState(140),
    [discoveryPagesPerQuery, setDiscoveryPagesPerQuery] = useState(2),
    [discoveryQueries, setDiscoveryQueries] = useState(""),
    [discoveryRunning, setDiscoveryRunning] = useState(false),
    [lastDiscoveryRun, setLastDiscoveryRun] = useState<any>(),
    [identityEvidence, setIdentityEvidence] = useState<
      Record<
        string,
        { bidirectional: string; corroborating: string; confidence: string; reason: string }
      >
    >({});
  const load = useCallback(
    (requestedPage = 1) =>
      Promise.all([
        axios.get(base + "/audit-queue", {
          headers: headers(),
          params: {
            page: requestedPage,
            pageSize: 25,
          },
        }),
        axios.get(base + "/status", { headers: headers() }),
        axios.get(base + "/legal-checklist", { headers: headers() }),
      ])
        .then(([q, s, l]) => {
          setItems(q.data.items || []);
          setQueuePage(q.data.page || 1);
          setQueueTotal(q.data.total || 0);
          setQueueTotalPages(q.data.totalPages || 0);
          setStatus(s.data);
          setLegal(l.data || {});
          setError("");
        })
        .catch(() =>
          setError("Admin access and a named staff identity are required."),
        ),
    [],
  );
  useEffect(() => {
    if (localStorage.getItem("apiToken")) void load(1);
  }, [load]);
  const connect = (e: React.FormEvent) => {
    e.preventDefault();
    localStorage.setItem("apiToken", token.trim());
    localStorage.setItem("observerUserEmail", staff.trim());
    void load(1);
  };
  const post = async (path: string, body: any = {}) => {
    try {
      await axios.post(base + path, body, { headers: headers() });
      await load(queuePage);
      return true;
    } catch (e: any) {
      alert(e?.response?.data?.error || "Action failed");
      return false;
    }
  };
  const startDiscovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setDiscoveryRunning(true);
    try {
      const queries = discoveryQueries
        .split("\n")
        .map((query) => query.trim())
        .filter(Boolean);
      const response = await axios.post(
        base + "/discovery/youtube",
        {
          queries,
          maximum: discoveryMaximum,
          pagesPerQuery: discoveryPagesPerQuery,
          useAI: true,
        },
        { headers: headers() },
      );
      let run = response.data.run;
      setLastDiscoveryRun(run);
      for (
        let attempt = 0;
        attempt < 150 && run.state === "running";
        attempt += 1
      ) {
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
        const current = await axios.get(base + "/discovery/runs/" + run.id, {
          headers: headers(),
        });
        run = current.data.run;
        setLastDiscoveryRun(run);
      }
      await load(1);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Discovery run failed.");
    } finally {
      setDiscoveryRunning(false);
    }
  };
  const evidenceFor = (id: string) =>
    identityEvidence[id] || {
      bidirectional: "",
      corroborating: "",
      confidence: "",
      reason: "",
    };
  const updateEvidence = (
    id: string,
    patch: Partial<{
      bidirectional: string;
      corroborating: string;
      confidence: string;
      reason: string;
    }>,
  ) =>
    setIdentityEvidence((current) => ({
      ...current,
      [id]: { ...evidenceFor(id), ...patch },
    }));
  return (
    <div className="ot-page">
      <header className="ot-page-head">
        <span className="section-kicker">Trust operations</span>
        <h1>Automated publication audit &amp; safety holds</h1>
        <p>
          Factual records publish through AutoPublicationGate without per-listing approval. Use this surface to sample categories, inspect evidence, correct, recategorise, hold, or unpublish. Reviews and judgments remain human-gated.
        </p>
      </header>
      <form className="ot-form" onSubmit={connect}>
        <label>
          Staff identity
          <input
            required
            type="email"
            autoComplete="username"
            value={staff}
            onChange={(e) => setStaff(e.target.value)}
            placeholder="name@observertheory.com"
          />
        </label>
        <label>
          Staff access token
          <input
            required
            type="password"
            autoComplete="current-password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </label>
        <button>Open trust-operations queue</button>
      </form>
      {status?.publicationThrottled && (
        <div className="ot-admin-banner" role="alert">
          <b>Review-dispute SLA alert</b>
          <p>{status.banner}</p>
        </div>
      )}
      {!status?.reviewCapacityCertified && status && (
        <div className="ot-admin-banner">
          <b>Review publication remains disabled</b>
          <p>
            The verified review funnel may accept submissions, but REVIEWS_LIVE must remain off until legal sign-off and sustainable 48-hour dispute capacity are recorded.
          </p>
        </div>
      )}
      {status && !(legal?.ukDefamationRead && legal?.ukGdprRead && legal?.intermediaryRead && legal?.evidenceRead) && <form className="ot-admin-banner" onSubmit={async (event) => { event.preventDefault(); await axios.post(base+"/legal-checklist", legal, { headers: headers() }); await load(queuePage); }}><b>Verdict-lane legal read still required</b><p>This does not block factual listings. Complete the UK defamation, UK-GDPR, intermediary, and evidence-retention read before enabling REVIEWS_LIVE.</p>{[["ukDefamationRead","UK defamation"],["ukGdprRead","UK-GDPR"],["intermediaryRead","Intermediary posture"],["evidenceRead","Evidence retention"]].map(([key,label]) => <label key={key}><input type="checkbox" checked={Boolean(legal?.[key])} onChange={(e) => setLegal({ ...legal, [key]: e.target.checked })} /> {label}</label>)}<button>Save legal checklist</button></form>}
      {error && <p className="ot-error">{error}</p>}
      {status && (
        <form className="ot-form" onSubmit={startDiscovery}>
          <fieldset>
            <legend>Discover public practitioner channels</legend>
            <p>
              Searches the official YouTube Data API, deduplicates IDs, enriches official sites, and feeds candidates directly into the versioned factual publication gate. Low-confidence categories publish Tier B and enter this sampling surface.
            </p>
            <label>
              Maximum candidates
              <input
                type="number"
                min={1}
                max={200}
                value={discoveryMaximum}
                onChange={(e) => setDiscoveryMaximum(Number(e.target.value))}
              />
            </label>
            <label>
              Search depth per query
              <input
                type="number"
                min={1}
                max={3}
                value={discoveryPagesPerQuery}
                onChange={(e) =>
                  setDiscoveryPagesPerQuery(Number(e.target.value))
                }
              />
              <small>
                Existing YouTube channel IDs are excluded before the run limit
                is applied. Use 2–3 pages for continuation batches.
              </small>
            </label>
            <label>
              Optional query families, one per line
              <textarea
                value={discoveryQueries}
                onChange={(e) => setDiscoveryQueries(e.target.value)}
                placeholder="Leave blank for the reviewed manifestation and law-of-attraction launch set."
              />
            </label>
            <button disabled={discoveryRunning}>
              {discoveryRunning ? "Discovering…" : "Prepare candidate queue"}
            </button>
            {lastDiscoveryRun && (
              <p role="status">
                Imported {lastDiscoveryRun.importedCount}; gate-ready{" "}
                {lastDiscoveryRun.readyForReviewCount}; needs research{" "}
                {lastDiscoveryRun.needsResearchCount}; duplicates{" "}
                {lastDiscoveryRun.duplicateCount}.
              </p>
            )}
          </fieldset>
        </form>
      )}
      {status && queueTotal > 0 && (
        <nav className="ot-admin-pagination" aria-label="Audit queue pages">
          <p>
            {queueTotal} candidates · Alphabetical · Page {queuePage} of{" "}
            {queueTotalPages}
          </p>
          <div>
            <button disabled={queuePage === 1} onClick={() => void load(1)}>
              First
            </button>
            <button
              disabled={queuePage === 1}
              onClick={() => void load(queuePage - 1)}
            >
              Previous
            </button>
            {Array.from(
              { length: queueTotalPages },
              (_, index) => index + 1,
            ).map((page) => (
              <button
                aria-current={page === queuePage ? "page" : undefined}
                className={page === queuePage ? "active" : ""}
                key={page}
                onClick={() => void load(page)}
              >
                {page}
              </button>
            ))}
            <button
              disabled={queuePage === queueTotalPages}
              onClick={() => void load(queuePage + 1)}
            >
              Next
            </button>
            <button
              disabled={queuePage === queueTotalPages}
              onClick={() => void load(queueTotalPages)}
            >
              Last
            </button>
          </div>
        </nav>
      )}
      <div className="ot-results">
        {items.map((row) => (
          <article className="ot-result" key={row.entity.id}>
            <span>
              {row.entity.inventoryTier || "Not published"} · {row.entity.state}
              {row.entity.categoryAuditRequired ? " · category sample" : ""}
            </span>
            <h2>{row.entity.displayName}</h2>
            <p>
              {row.entity.entityType} ·{" "}
              {(row.entity.practiceTags || []).join(", ")}
            </p>
            {row.research && (
              <section className="ot-provenance">
                <b>Classifier evidence — sampling audit</b>
                <p>{row.research.rationale}</p>
                <p>
                  Confidence{" "}
                  {Math.round(row.research.relevanceConfidence * 100)}% ·
                  Queries:{" "}
                  {(row.research.queryMatches || []).join(", ") ||
                    "Not recorded"}
                </p>
                <p>
                  Observed commercial terms:{" "}
                  {(row.research.commercialSignals || []).join(", ") ||
                    "None — research required"}
                </p>
                {(row.research.externalUrls || []).length > 0 && (
                  <ul>
                    {row.research.externalUrls.map((url: string) => (
                      <li key={url}>
                        <a href={url} target="_blank" rel="noreferrer">
                          Candidate official link
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}
            {(row.sources || []).length > 0 && (
              <p>
                Sources:{" "}
                {row.sources.map((source: any, index: number) => (
                  <React.Fragment key={source.id}>
                    {index > 0 ? ", " : ""}
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.title || source.sourceKind}
                    </a>
                  </React.Fragment>
                ))}
              </p>
            )}
            <p className="ot-screening-note">
              This is an after-publication sampling and safety queue, never a required approval step.
            </p>
            <details className="ot-publication-gate">
              <summary>Evidence audit, corrections, and safety overrides</summary>
              <table>
                <thead>
                  <tr>
                    <th>Confidence</th>
                    <th>Field</th>
                    <th>Value</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {row.assertions.map((a: any) => (
                    <tr key={a.id}>
                      <td>{Math.round(a.confidence * 100)}%</td>
                      <td>{a.field}</td>
                      <td>{String(a.value)}</td>
                      <td>
                        {a.state === "pending_human_approval" && (
                          <button
                            onClick={() =>
                              post("/assertions/" + a.id + "/approve", {
                                reason: evidenceFor(row.entity.id).reason,
                              })
                            }
                          >
                            Confirm
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(row.platformAccounts || []).map((a: any) => (
                    <tr key={a.id}>
                      <td>{Math.round(a.confidence * 100)}%</td>
                      <td>{a.platform} account</td>
                      <td>
                        <a href={a.url}>{a.handle}</a>
                      </td>
                      <td>
                        {a.status !== "confirmed" && (
                          <button
                            onClick={() =>
                              post("/platform-accounts/" + a.id + "/confirm", {
                                reason: evidenceFor(row.entity.id).reason,
                              })
                            }
                          >
                            Confirm link
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <fieldset>
                <legend>Optional identity evidence correction</legend>
                <label>
                  Bidirectional link evidence
                  <input
                    type="url"
                    required
                    value={evidenceFor(row.entity.id).bidirectional}
                    onChange={(e) =>
                      updateEvidence(row.entity.id, {
                        bidirectional: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Independent corroborating evidence
                  <input
                    type="url"
                    required
                    value={evidenceFor(row.entity.id).corroborating}
                    onChange={(e) =>
                      updateEvidence(row.entity.id, {
                        corroborating: e.target.value,
                      })
                    }
                  />
                </label>
                <label>
                  Corrected category confidence (optional)
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={evidenceFor(row.entity.id).confidence}
                    onChange={(e) => updateEvidence(row.entity.id, { confidence: e.target.value })}
                  />
                </label>
                <label>
                  Decision reason
                  <input
                    required
                    value={evidenceFor(row.entity.id).reason}
                    onChange={(e) =>
                      updateEvidence(row.entity.id, { reason: e.target.value })
                    }
                  />
                </label>
                <small>
                  Use this only to correct automated identity evidence during an audit.
                </small>
              </fieldset>
              <div className="ot-admin-actions">
                <button
                  onClick={async () => {
                    const evidence = evidenceFor(row.entity.id);
                    const why = evidence.reason;
                    if (!why) return;
                    const identitySignals = evidence.bidirectional && evidence.corroborating ? [
                      { kind: "bidirectional_website", value: evidence.bidirectional, bidirectional: true },
                      { kind: "corroborating_source", value: evidence.corroborating, bidirectional: false },
                    ] : undefined;
                    await post("/entities/" + row.entity.id + "/audit-correction", {
                      reason: why,
                      identitySignals,
                      categoryConfidence: evidence.confidence === "" ? undefined : Number(evidence.confidence),
                    });
                  }}
                >
                  Save correction and re-run gate
                </button>
                <button
                  onClick={() =>
                    post("/entities/" + row.entity.id + "/park", {
                      reason: evidenceFor(row.entity.id).reason,
                    })
                  }
                >
                  Park
                </button>
                <button
                  onClick={() =>
                    post("/entities/" + row.entity.id + "/decline", {
                      reason: evidenceFor(row.entity.id).reason,
                    })
                  }
                >
                  Decline
                </button>
              </div>
            </details>
          </article>
        ))}
      </div>
      {status && queueTotalPages > 1 && (
        <nav
          className="ot-admin-pagination ot-admin-pagination-bottom"
          aria-label="Audit queue page navigation"
        >
          <button
            disabled={queuePage === 1}
            onClick={() => void load(queuePage - 1)}
          >
            Previous page
          </button>
          <span>
            Page {queuePage} of {queueTotalPages}
          </span>
          <button
            disabled={queuePage === queueTotalPages}
            onClick={() => void load(queuePage + 1)}
          >
            Next page
          </button>
        </nav>
      )}
    </div>
  );
}
